/**
 * Assessment Detail Page
 *
 * Displays a single assessment with:
 * - Score gauges (Overall, Compliance, Efficiency, Safety)
 * - Step-by-step breakdown (v1) OR question responses (v2)
 * - Manager notes and override functionality
 * - PDF download
 *
 * Supports two scoring versions:
 * - v1 (scoringVersion=1): Legacy step-based scoring with 50+ steps
 * - v2 (scoringVersion=2): Question-based scoring with 10-13 questions per job
 */

import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Edit3,
  User,
  FileText,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Target,
  TrendingUp,
  Shield,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Award,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  useAssessment,
  useOverrideAssessment,
  useDownloadAssessmentPdf,
} from '../../hooks/useAssessments';
// A10.7 — deep import bypasses the barrel `@guild/config` re-export, which
// transitively pulls Node-only `process.env` reads (and `@google/genai` via
// ai.config) that fail tsc under the web app's `"types": ["vite/client"]`
// setup. The coverage config file is intentionally pure-data; safe here.
// Path matches the package.json `exports: { './*': './src/*' }` rule.
import { ASSESSMENT_COVERAGE_CONFIG } from '@guild/config/assessment-coverage.config.js';
import { getScoreColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { useRoleLevel } from '../../lib/hooks/useRoleLevel';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { VideoReviewTab } from '../../components/video/VideoReviewTab';

// ============================================
// TYPES
// ============================================

interface StepData {
  id: string;
  sopStepId: string;
  passed: boolean;
  score: number;
  feedback: string | null;
  evidenceS3Key: string | null;
  timestamp: number | null;
  sopStep: {
    stepNumber: number;
    description: string;
    isSafetyCritical: boolean;
  };
}

interface QuestionResponseData {
  id: string;
  questionId: string;
  rubricScore: number;
  feedback: string | null;
  evidenceS3Key: string | null;
  videoTimestamp: number | null;
  question: {
    questionNumber: number;
    questionText: string;
    category: 'compliance' | 'efficiency' | 'safety';
    observationGuidance: string | null;
    isRequired: boolean;
  };
}

// Stats types for v1 and v2 assessments
interface V1Stats {
  isV2: false;
  total: number;
  passed: number;
  failed: number;
  safetyTotal: number;
  safetyPassed: number;
}

interface V2Stats {
  isV2: true;
  total: number;
  passed: number;
  failed: number;
  categories: {
    compliance: { total: number; passed: number };
    efficiency: { total: number; passed: number };
    safety: { total: number; passed: number };
  };
}

type AssessmentStats = V1Stats | V2Stats | null;

// ============================================
// HELPER FUNCTIONS
// ============================================


function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

function getRubricLabel(score: number): string {
  switch (score) {
    case 100: return 'Exemplary';
    case 80: return 'Proficient';
    case 60: return 'Developing';
    case 40: return 'Struggling';
    case 20: return 'Needs Training';
    case 0: return 'Untrained';
    default: return `${score}`;
  }
}

function getRubricColor(score: number): { bg: string; text: string; border: string } {
  if (score >= 80) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (score >= 60) return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' };
  if (score >= 40) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
  return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
}

function getCategoryConfig(category: 'compliance' | 'efficiency' | 'safety') {
  switch (category) {
    case 'compliance':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: Target };
    case 'efficiency':
      return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: TrendingUp };
    case 'safety':
      return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Shield };
  }
}

// ============================================
// CIRCULAR SCORE GAUGE COMPONENT
// ============================================

interface ScoreGaugeProps {
  score: number;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  accentColor?: string;
  icon?: React.ReactNode;
}

function ScoreGauge({ score, label, size = 'md', accentColor, icon }: ScoreGaugeProps) {
  const dimensions = {
    sm: { size: 80, stroke: 6, fontSize: 'text-lg', labelSize: 'text-[10px]' },
    md: { size: 100, stroke: 8, fontSize: 'text-2xl', labelSize: 'text-xs' },
    lg: { size: 140, stroke: 10, fontSize: 'text-4xl', labelSize: 'text-sm' },
  };

  const { size: svgSize, stroke, fontSize, labelSize } = dimensions[size];
  const radius = (svgSize - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const color = accentColor || getScoreColor(score, 'ring');

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: svgSize, height: svgSize }}>
        <svg width={svgSize} height={svgSize} className="-rotate-90">
          {/* Background circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={stroke}
          />
          {/* Progress circle */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {icon && <div className="mb-0.5 opacity-60">{icon}</div>}
          <span className={`font-bold ${fontSize}`} style={{ color }}>
            {score.toFixed(0)}
          </span>
        </div>
      </div>
      <span className={`mt-2 font-semibold text-gray-600 uppercase tracking-wider ${labelSize}`}>
        {label}
      </span>
    </div>
  );
}

// ============================================
// STEP ROW COMPONENT
// ============================================

interface StepRowProps {
  step: StepData;
  isExpanded: boolean;
  onToggle: () => void;
}

function StepRow({ step, isExpanded, onToggle }: StepRowProps) {
  const hasFeedback = step.feedback && step.feedback.trim().length > 0;

  return (
    <div
      className={`border-l-4 transition-all duration-200 ${
        step.passed ? 'border-l-emerald-500' : 'border-l-red-500'
      }`}
    >
      <div
        className={`flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50/80 transition-colors ${
          hasFeedback ? '' : 'cursor-default'
        }`}
        onClick={hasFeedback ? onToggle : undefined}
      >
        {/* Step number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-600">{step.sopStep.stepNumber}</span>
        </div>

        {/* Description */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 line-clamp-2">{step.sopStep.description}</p>
        </div>

        {/* Safety badge */}
        {step.sopStep.isSafetyCritical && (
          <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded-md text-xs font-semibold">
            <ShieldAlert className="w-3 h-3" />
            Safety
          </span>
        )}

        {/* Score badge */}
        <div
          className={`flex-shrink-0 px-3 py-1 rounded-lg text-sm font-semibold ${
            step.passed
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {step.score.toFixed(0)}
        </div>

        {/* Pass/Fail icon */}
        <div className="flex-shrink-0">
          {step.passed ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
        </div>

        {/* Expand indicator */}
        {hasFeedback && (
          <div className="flex-shrink-0 text-gray-400">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
        )}
      </div>

      {/* Feedback section */}
      {hasFeedback && isExpanded && (
        <div className="px-4 pb-4 pl-16">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <MessageSquare className="w-3 h-3" />
              Feedback
            </div>
            <p className="text-sm text-gray-700">{step.feedback}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// QUESTION RESPONSE ROW COMPONENT (V2)
// ============================================

interface QuestionResponseRowProps {
  response: QuestionResponseData;
  isExpanded: boolean;
  onToggle: () => void;
}

function QuestionResponseRow({ response, isExpanded, onToggle }: QuestionResponseRowProps) {
  const hasFeedback = response.feedback && response.feedback.trim().length > 0;
  const rubricColor = getRubricColor(response.rubricScore);
  const categoryConfig = getCategoryConfig(response.question.category);
  const CategoryIcon = categoryConfig.icon;

  return (
    <div className="border-l-4 transition-all duration-200" style={{ borderLeftColor: response.rubricScore >= 60 ? '#10b981' : '#ef4444' }}>
      <div
        className={`flex items-start gap-4 px-4 py-4 cursor-pointer hover:bg-gray-50/80 transition-colors ${
          hasFeedback || response.question.observationGuidance ? '' : 'cursor-default'
        }`}
        onClick={(hasFeedback || response.question.observationGuidance) ? onToggle : undefined}
      >
        {/* Question number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-600">Q{response.question.questionNumber}</span>
        </div>

        {/* Question text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900">{response.question.questionText}</p>
          {/* Category badge */}
          <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold ${categoryConfig.bg} ${categoryConfig.text}`}>
            <CategoryIcon className="w-3 h-3" />
            {response.question.category.charAt(0).toUpperCase() + response.question.category.slice(1)}
          </span>
        </div>

        {/* Rubric score badge */}
        <div className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold border ${rubricColor.bg} ${rubricColor.text} ${rubricColor.border}`}>
          {getRubricLabel(response.rubricScore)}
          <span className="ml-1 opacity-70">({response.rubricScore})</span>
        </div>

        {/* Expand indicator */}
        {(hasFeedback || response.question.observationGuidance) && (
          <div className="flex-shrink-0 text-gray-400 mt-1">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
        )}
      </div>

      {/* Expanded section */}
      {isExpanded && (hasFeedback || response.question.observationGuidance) && (
        <div className="px-4 pb-4 pl-16 space-y-3">
          {/* Observation guidance */}
          {response.question.observationGuidance && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                <Target className="w-3 h-3" />
                Observation Guidance
              </div>
              <p className="text-sm text-gray-700">{response.question.observationGuidance}</p>
            </div>
          )}
          {/* Feedback */}
          {hasFeedback && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                <MessageSquare className="w-3 h-3" />
                Feedback
              </div>
              <p className="text-sm text-gray-700">{response.feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// OVERRIDE MODAL COMPONENT
// ============================================

interface OverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  assessmentId: string;
  currentScores: {
    compliance: number;
    efficiency: number;
    safety: number;
  };
  currentNotes: string | null;
}

function OverrideModal({
  isOpen,
  onClose,
  assessmentId,
  currentScores,
  currentNotes,
}: OverrideModalProps) {
  const [compliance, setCompliance] = useState(currentScores.compliance);
  const [efficiency, setEfficiency] = useState(currentScores.efficiency);
  const [safety, setSafety] = useState(currentScores.safety);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState(currentNotes || '');

  const overrideMutation = useOverrideAssessment();

  const handleSubmit = () => {
    overrideMutation.mutate(
      {
        id: assessmentId,
        data: {
          complianceScore: compliance,
          efficiencyScore: efficiency,
          safetyScore: safety,
          reason,
          managerNotes: notes,
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const isValid = reason.length >= 1 && reason.length <= 500;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#00674a]/5 to-[#c5e8c2]/10">
          <h2 className="text-lg font-bold text-gray-900">Override Scores</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Adjust scores with required justification
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Score sliders */}
          <div className="space-y-5">
            {/* Compliance */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                    <Target className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Compliance</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(compliance, 'text')}`}>
                  {compliance}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={compliance}
                onChange={(e) => setCompliance(parseInt(e.target.value, 10))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 ${compliance}%, #e5e7eb ${compliance}%)`,
                }}
              />
            </div>

            {/* Efficiency */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-[#c5e8c2] flex items-center justify-center">
                    <TrendingUp className="w-3 h-3 text-[#00674a]" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Efficiency</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(efficiency, 'text')}`}>
                  {efficiency}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={efficiency}
                onChange={(e) => setEfficiency(parseInt(e.target.value, 10))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #00674a ${efficiency}%, #e5e7eb ${efficiency}%)`,
                }}
              />
            </div>

            {/* Safety */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center">
                    <Shield className="w-3 h-3 text-red-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Safety</span>
                </div>
                <span className={`text-lg font-bold ${getScoreColor(safety, 'text')}`}>
                  {safety}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={safety}
                onChange={(e) => setSafety(parseInt(e.target.value, 10))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #dc2626 ${safety}%, #e5e7eb ${safety}%)`,
                }}
              />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">
                Reason for Override <span className="text-red-500">*</span>
              </span>
              <span className="text-xs text-gray-400 ml-2">(1-500 characters)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you are adjusting these scores..."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 text-sm resize-none"
            />
            <div className="text-right text-xs text-gray-400">{reason.length} / 500</div>
          </div>

          {/* Manager Notes */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">Manager Notes</span>
              <span className="text-xs text-gray-400 ml-2">(optional, max 5000)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              maxLength={5000}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || overrideMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00674a] text-white text-sm font-semibold rounded-lg hover:bg-[#00674a]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {overrideMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Override
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function AssessmentDetailPage() {
  useDocumentTitle('Assessment Detail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);
  const [activeTab, setActiveTab] = useState<'summary' | 'video-review' | 'steps' | 'questions' | 'notes'>('summary');

  // Get user role for conditional tab rendering.
  // Phase 19.S frontend migration (May 6, 2026) — role flows from
  // DB-canonical /api/me via useRoleLevel, not the JWT claim.
  const { isTrainerOrHigher: isTrainerOrAbove } = useRoleLevel();
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  // Fetch assessment data
  const { data: assessment, isLoading, error } = useAssessment(id ?? null);
  const downloadPdf = useDownloadAssessmentPdf();

  // Calculate summary stats based on scoring version
  const stats: AssessmentStats = useMemo(() => {
    // V2: Question-based scoring
    if (assessment?.scoringVersion === 2 && assessment?.questionResponses) {
      const responses = assessment.questionResponses as QuestionResponseData[];
      const total = responses.length;
      const passed = responses.filter((r) => r.rubricScore >= 60).length;
      const failed = total - passed;

      // Group by category
      const byCategory = {
        compliance: responses.filter((r) => r.question.category === 'compliance'),
        efficiency: responses.filter((r) => r.question.category === 'efficiency'),
        safety: responses.filter((r) => r.question.category === 'safety'),
      };

      const v2Stats: V2Stats = {
        isV2: true,
        total,
        passed,
        failed,
        categories: {
          compliance: {
            total: byCategory.compliance.length,
            passed: byCategory.compliance.filter((r) => r.rubricScore >= 60).length,
          },
          efficiency: {
            total: byCategory.efficiency.length,
            passed: byCategory.efficiency.filter((r) => r.rubricScore >= 60).length,
          },
          safety: {
            total: byCategory.safety.length,
            passed: byCategory.safety.filter((r) => r.rubricScore >= 60).length,
          },
        },
      };
      return v2Stats;
    }

    // V1: Step-based scoring
    if (!assessment?.steps) return null;

    const total = assessment.steps.length;
    const passed = assessment.steps.filter((s) => s.passed).length;
    const failed = total - passed;
    const safetySteps = assessment.steps.filter((s) => s.sopStep.isSafetyCritical);
    const safetyPassed = safetySteps.filter((s) => s.passed).length;

    const v1Stats: V1Stats = { isV2: false, total, passed, failed, safetyTotal: safetySteps.length, safetyPassed };
    return v1Stats;
  }, [assessment?.steps, assessment?.questionResponses, assessment?.scoringVersion]);

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  const handleDownloadPdf = () => {
    if (id) {
      downloadPdf.mutate(id);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-sm text-gray-500">Loading assessment...</p>
      </div>
    );
  }

  // Error state
  if (error || !assessment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">Assessment Not Found</h2>
        <p className="text-sm text-gray-500 mt-1">
          The assessment you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate('/assessments')}
          className="mt-6 px-4 py-2 bg-[#00674a] text-white text-sm font-semibold rounded-lg hover:bg-[#00674a]/90 transition-colors"
        >
          Back to Assessments
        </button>
      </div>
    );
  }

  // NOTE: this `>= 70` literal is inconsistent with `getScoreColor`'s 80/60
  // bands. Pre-existing and deliberately left untouched by A10 (Rule 3) — the
  // coverage logic below reads coverage only and never this value.
  const isPassed = assessment.overallScore >= 70;

  // ---------- A10.7: step coverage ----------
  // Branch on `scoringVersion` directly, NOT on `stats.isV2`: `stats` falls
  // through to the V1 shape when a v2 assessment has no questionResponses, so
  // `!stats.isV2` would render a coverage tile on a v2 row.
  // Both counts must be non-null before any arithmetic — NULL means "coverage
  // unknown" (pre-A10 or manual) and must not become `NaN%` or `0 / 0`.
  const hasCoverage =
    assessment.scoringVersion !== 2 &&
    typeof assessment.observedStepCount === 'number' &&
    typeof assessment.totalStepCount === 'number' &&
    assessment.totalStepCount > 0;
  const coverageRatio = hasCoverage
    ? assessment.observedStepCount! / assessment.totalStepCount!
    : null;
  const coveragePct = coverageRatio !== null ? Math.round(coverageRatio * 100) : null;
  // PARAMETRIC-DISPATCH: ASSESSMENT_COVERAGE_CONFIG.ENABLED + ASSESSMENT_COVERAGE_CONFIG.WARN_BELOW
  // Strict `<` — a ratio of exactly WARN_BELOW is not below it, so no banner.
  const isLowCoverage =
    ASSESSMENT_COVERAGE_CONFIG.ENABLED &&
    coverageRatio !== null &&
    coverageRatio < ASSESSMENT_COVERAGE_CONFIG.WARN_BELOW;

  return (
    <div className="space-y-6 pb-8">
      {/* Back navigation */}
      <Link
        to="/assessments"
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
        Back to Assessments
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 shadow-lg shadow-[#00674a]/20">
              <Award size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                Assessment Details
              </h1>
              <p className="text-sm text-gray-500">
                {assessment.operator.firstName} {assessment.operator.lastName} •{' '}
                {assessment.sop.title}
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowOverrideModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            <Edit3 size={16} />
            Override
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloadPdf.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00674a] text-white rounded-xl text-sm font-semibold shadow-lg shadow-[#00674a]/25 hover:bg-[#00674a]/90 hover:shadow-xl hover:shadow-[#00674a]/30 transition-all disabled:opacity-50"
          >
            {downloadPdf.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Download PDF
          </button>
        </div>
      </div>

      {/*
        Phase 23 §3.4: "SOP has been updated since this assessment was scored"
        banner. Renders ONLY when both ids are non-null AND differ — handles
        the no-render cases per spec:
          (a) sopVersionId null  -> legacy assessment (pre-23.1.2 backfill)
          (b) sopCurrentVersionId null -> SOP has no published version yet
          (c) values match -> assessment is on the current version
          (d) FK-orphaned current version -> sopCurrentVersionNumber is null
              (the JOIN-and-lookup chain returned no row); we still skip the
              banner because we cannot show a meaningful label.
      */}
      {assessment.sopVersionId &&
        assessment.sopCurrentVersionId &&
        assessment.sopVersionId !== assessment.sopCurrentVersionId &&
        assessment.sopCurrentVersionNumber && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="flex-1 text-sm">
              <p className="font-medium">
                SOP has been updated since this assessment was scored — current
                version: v{assessment.sopCurrentVersionNumber}
              </p>
            </div>
            <Link
              to={`/sops/${assessment.sop.id}`}
              className="flex-shrink-0 text-sm font-semibold text-amber-900 underline-offset-2 hover:underline"
            >
              View latest
            </Link>
          </div>
        )}

      {/* A10.7: partial step-coverage banner.
          Hidden when (a) coverage is unknown — pre-A10 rows and manual
          assessments, both NULL; (b) the assessment is v2; (c) coverage is at
          or above WARN_BELOW; (d) the kill switch is off.
          Palette note: `getScoreColor(score,'badge')` returns amber-100/700 for
          the 60-79 band, so this banner uses the -50/-200/-900 tokens to stay
          readable as a separate signal next to a mid-score badge. */}
      {isLowCoverage && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="flex-1 text-sm">
            <p className="font-medium">
              Partial coverage — only {assessment.observedStepCount} of this Job&apos;s{' '}
              {assessment.totalStepCount} steps ({coveragePct}%) were observed in the video
            </p>
            <p className="mt-1 text-amber-800">
              Scores below reflect the observed steps only. The rest of the Job may
              have happened outside the capture window.
            </p>
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column - Metadata & Scores */}
        <div className="lg:col-span-2 space-y-6">
          {/* Metadata card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-[#00674a]/5 to-[#c5e8c2]/10">
              <h3 className="font-bold text-gray-900">Assessment Info</h3>
            </div>
            <div className="p-5 space-y-4">
              {/* Operator */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#c5e8c2]/30 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#00674a]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                    Operator
                  </p>
                  <p className="font-semibold text-gray-900">
                    {assessment.operator.firstName} {assessment.operator.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{assessment.operator.employeeId}</p>
                </div>
              </div>

              {/* SOP */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                    SOP
                  </p>
                  <p className="font-semibold text-gray-900 line-clamp-1">
                    {assessment.sop.title}
                  </p>
                </div>
              </div>

              {/* Type & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                      Type
                    </p>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        assessment.assessmentType === 'auto'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-purple-50 text-purple-700'
                      }`}
                    >
                      {assessment.assessmentType === 'auto' ? 'Automated' : 'Manual'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                      Date
                    </p>
                    <p className="text-sm font-medium text-gray-700">
                      {formatDate(assessment.assessedAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Assessor */}
              {assessment.assessedByUser && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Assessed by{' '}
                    <span className="font-semibold text-gray-700">
                      {assessment.assessedByUser.fullName}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Pass/Fail Status.
              A10.7: below WARN_BELOW coverage this becomes a neutral amber
              partial-coverage state instead. A proficiency claim asserts the
              operator did the job — it must not be made from a minority of the
              Job's steps. */}
          <div
            className={`rounded-2xl p-5 border ${
              isLowCoverage
                ? 'bg-amber-50 border-amber-200'
                : isPassed
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isLowCoverage ? 'bg-amber-100' : isPassed ? 'bg-emerald-100' : 'bg-red-100'
                }`}
              >
                {isLowCoverage ? (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                ) : isPassed ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div>
                <p
                  className={`text-lg font-bold ${
                    isLowCoverage ? 'text-amber-700' : isPassed ? 'text-emerald-700' : 'text-red-700'
                  }`}
                >
                  {isLowCoverage ? 'PARTIAL COVERAGE' : isPassed ? 'PASSED' : 'FAILED'}
                </p>
                <p
                  className={`text-sm ${
                    isLowCoverage ? 'text-amber-600' : isPassed ? 'text-emerald-600' : 'text-red-600'
                  }`}
                >
                  {isLowCoverage
                    ? `Only ${coveragePct}% of this Job's steps were observed — score reflects the observed steps only`
                    : isPassed
                      ? 'Operator meets proficiency requirements'
                      : 'Additional training recommended'}
                </p>
              </div>
            </div>
          </div>

          {/* Score Gauges */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            {/* Overall Score - Large */}
            <div className="flex justify-center mb-8 pb-6 border-b border-gray-100">
              <ScoreGauge
                score={assessment.overallScore}
                label="Overall"
                size="lg"
                icon={<Award className="w-5 h-5" />}
              />
            </div>

            {/* Category Scores - Grid */}
            <div className="grid grid-cols-3 gap-4">
              <ScoreGauge
                score={assessment.complianceScore}
                label="Compliance"
                size="sm"
                accentColor="#3b82f6"
                icon={<Target className="w-3 h-3" />}
              />
              <ScoreGauge
                score={assessment.efficiencyScore}
                label="Efficiency"
                size="sm"
                accentColor="#00674a"
                icon={<TrendingUp className="w-3 h-3" />}
              />
              <ScoreGauge
                score={assessment.safetyScore}
                label="Safety"
                size="sm"
                accentColor="#dc2626"
                icon={<Shield className="w-3 h-3" />}
              />
            </div>
          </div>
        </div>

        {/* Right column - Tabs */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Tab navigation - conditional based on scoring version */}
            <div className="flex border-b border-gray-100">
              {[
                { key: 'summary', label: 'Summary' },
                // Video Review tab - trainer+ and automated assessments only
                ...(isTrainerOrAbove && assessment.assessmentType !== 'manual'
                  ? [{ key: 'video-review', label: 'Video Review' }]
                  : []),
                // V2 shows "Questions", V1 shows "Step Breakdown"
                assessment.scoringVersion === 2
                  ? { key: 'questions', label: 'Questions' }
                  : { key: 'steps', label: 'Step Breakdown' },
                { key: 'notes', label: 'Notes' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex-1 px-4 py-4 text-sm font-semibold transition-all relative ${
                    activeTab === tab.key
                      ? 'text-[#00674a]'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00674a]" />
                  )}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-6">
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  {/* Quick Stats - V2 (Question-based) */}
                  {stats && stats.isV2 && (
                    <div className="space-y-4">
                      {/* Overall question stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                          <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                            Questions
                          </div>
                        </div>
                        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                          <div className="text-2xl font-bold text-emerald-700">{stats.passed}</div>
                          <div className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">
                            Passed (≥60)
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                          <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
                          <div className="text-xs text-red-600 font-semibold uppercase tracking-wider">
                            Needs Work
                          </div>
                        </div>
                      </div>

                      {/* Category breakdown */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Compliance */}
                        <div className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">
                              Compliance
                            </span>
                          </div>
                          <div className="text-xl font-bold text-emerald-700">
                            {stats.categories.compliance.passed}/{stats.categories.compliance.total}
                          </div>
                        </div>

                        {/* Efficiency */}
                        <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <span className="text-xs text-blue-700 font-semibold uppercase tracking-wider">
                              Efficiency
                            </span>
                          </div>
                          <div className="text-xl font-bold text-blue-700">
                            {stats.categories.efficiency.passed}/{stats.categories.efficiency.total}
                          </div>
                        </div>

                        {/* Safety */}
                        <div className="bg-orange-50/50 rounded-xl p-4 border border-orange-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-orange-600" />
                            <span className="text-xs text-orange-700 font-semibold uppercase tracking-wider">
                              Safety
                            </span>
                          </div>
                          <div className="text-xl font-bold text-orange-700">
                            {stats.categories.safety.passed}/{stats.categories.safety.total}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick Stats - V1 (Step-based) */}
                  {stats && !stats.isV2 && (
                    <div
                      className={`grid grid-cols-2 gap-4 ${
                        hasCoverage ? 'sm:grid-cols-5' : 'sm:grid-cols-4'
                      }`}
                    >
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                          Total Steps
                        </div>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                        <div className="text-2xl font-bold text-emerald-700">{stats.passed}</div>
                        <div className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">
                          Passed
                        </div>
                      </div>
                      <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                        <div className="text-2xl font-bold text-red-700">{stats.failed}</div>
                        <div className="text-xs text-red-600 font-semibold uppercase tracking-wider">
                          Failed
                        </div>
                      </div>
                      <div
                        className={`rounded-xl p-4 border ${
                          stats.safetyPassed === stats.safetyTotal
                            ? 'bg-emerald-50 border-emerald-100'
                            : 'bg-red-50 border-red-100'
                        }`}
                      >
                        <div
                          className={`text-2xl font-bold ${
                            stats.safetyPassed === stats.safetyTotal
                              ? 'text-emerald-700'
                              : 'text-red-700'
                          }`}
                        >
                          {stats.safetyPassed}/{stats.safetyTotal}
                        </div>
                        <div
                          className={`text-xs font-semibold uppercase tracking-wider ${
                            stats.safetyPassed === stats.safetyTotal
                              ? 'text-emerald-600'
                              : 'text-red-600'
                          }`}
                        >
                          Safety
                        </div>
                      </div>

                      {/* A10.7: coverage tile — same X/Y shape as Safety above.
                          Rendered whenever coverage is known, on every v1
                          assessment, gated by no threshold: only the banner and
                          the proficiency wording are thresholded. */}
                      {hasCoverage && (
                        <div
                          className={`rounded-xl p-4 border ${
                            isLowCoverage
                              ? 'bg-amber-50 border-amber-100'
                              : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div
                            className={`text-2xl font-bold ${
                              isLowCoverage ? 'text-amber-700' : 'text-gray-900'
                            }`}
                          >
                            {assessment.observedStepCount}/{assessment.totalStepCount}
                          </div>
                          <div
                            className={`text-xs font-semibold uppercase tracking-wider ${
                              isLowCoverage ? 'text-amber-600' : 'text-gray-500'
                            }`}
                          >
                            Observed ({coveragePct}%)
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Summary */}
                  {assessment.aiSummary ? (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                      <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                        AI Summary
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {assessment.aiSummary}
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 text-center">
                      <p className="text-sm text-gray-500">No AI summary available for this assessment.</p>
                    </div>
                  )}

                  {/* Score breakdown */}
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Score Calculation
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Compliance (40%)</span>
                        <span className="font-semibold text-gray-900">
                          {assessment.complianceScore.toFixed(0)} × 0.4 ={' '}
                          {(assessment.complianceScore * 0.4).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Efficiency (30%)</span>
                        <span className="font-semibold text-gray-900">
                          {assessment.efficiencyScore.toFixed(0)} × 0.3 ={' '}
                          {(assessment.efficiencyScore * 0.3).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Safety (30%)</span>
                        <span className="font-semibold text-gray-900">
                          {assessment.safetyScore.toFixed(0)} × 0.3 ={' '}
                          {(assessment.safetyScore * 0.3).toFixed(1)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="font-semibold text-gray-900">Overall Score</span>
                        <span className={`font-bold ${getScoreColor(assessment.overallScore, 'text')}`}>
                          {assessment.overallScore.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Video Review Tab (Trainer+ and auto assessments only) */}
              {activeTab === 'video-review' && isTrainerOrAbove && assessment.assessmentType !== 'manual' && (
                <div className="animate-in fade-in duration-200 -mx-6 -my-6 p-6">
                  <VideoReviewTab assessmentId={assessment.id} />
                </div>
              )}

              {/* Steps Tab (V1) */}
              {activeTab === 'steps' && (
                <div className="animate-in fade-in duration-200">
                  {assessment.steps && assessment.steps.length > 0 ? (
                    <div className="divide-y divide-gray-100 -mx-6 -my-6">
                      {assessment.steps
                        .sort((a, b) => a.sopStep.stepNumber - b.sopStep.stepNumber)
                        .map((step) => (
                          <StepRow
                            key={step.id}
                            step={step}
                            isExpanded={expandedSteps.has(step.id)}
                            onToggle={() => toggleStep(step.id)}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">No step data available.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Questions Tab (V2) */}
              {activeTab === 'questions' && (
                <div className="animate-in fade-in duration-200">
                  {assessment.questionResponses && assessment.questionResponses.length > 0 ? (
                    <div className="space-y-6 -mx-6 -my-6">
                      {/* Group questions by category */}
                      {(['compliance', 'efficiency', 'safety'] as const).map((category) => {
                        const responses = (assessment.questionResponses as QuestionResponseData[])
                          .filter((r) => r.question.category === category)
                          .sort((a, b) => a.question.questionNumber - b.question.questionNumber);

                        if (responses.length === 0) return null;

                        const categoryConfig = getCategoryConfig(category);
                        const CategoryIcon = categoryConfig.icon;
                        const avgScore = responses.reduce((sum, r) => sum + r.rubricScore, 0) / responses.length;

                        return (
                          <div key={category}>
                            {/* Category header */}
                            <div className={`px-6 py-3 ${categoryConfig.bg} border-y ${categoryConfig.border} flex items-center justify-between`}>
                              <div className="flex items-center gap-2">
                                <CategoryIcon className={`w-4 h-4 ${categoryConfig.text}`} />
                                <span className={`font-semibold ${categoryConfig.text} capitalize`}>
                                  {category}
                                </span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({responses.length} questions)
                                </span>
                              </div>
                              <span className={`text-sm font-semibold ${categoryConfig.text}`}>
                                Avg: {avgScore.toFixed(0)}
                              </span>
                            </div>

                            {/* Category questions */}
                            <div className="divide-y divide-gray-100">
                              {responses.map((response) => (
                                <QuestionResponseRow
                                  key={response.id}
                                  response={response}
                                  isExpanded={expandedSteps.has(response.id)}
                                  onToggle={() => toggleStep(response.id)}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">No question responses available.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="animate-in fade-in duration-200 space-y-4">
                  {assessment.managerNotes ? (
                    <div className="p-4 bg-[#c5e8c2]/20 rounded-xl border border-[#c5e8c2]/40">
                      <div className="flex items-center gap-2 text-xs font-semibold text-[#00674a] uppercase tracking-wider mb-3">
                        <MessageSquare className="w-3 h-3" />
                        Manager Notes
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {assessment.managerNotes}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                        <MessageSquare className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No manager notes have been added.</p>
                      <button
                        onClick={() => setShowOverrideModal(true)}
                        className="mt-4 text-sm font-semibold text-[#00674a] hover:underline"
                      >
                        Add notes via Override
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Override Modal */}
      <OverrideModal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        assessmentId={assessment.id}
        currentScores={{
          compliance: assessment.complianceScore,
          efficiency: assessment.efficiencyScore,
          safety: assessment.safetyScore,
        }}
        currentNotes={assessment.managerNotes}
      />
    </div>
  );
}

export default AssessmentDetailPage;
